"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconPhoto,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function BrightSpringVsTrueSpringContent() {
  const t = useTranslations("brightSpringVsTrueSpring");

  // Get the subseason data
  const brightSpringData = colorSeasons.SPRING.subseasons.BRIGHT;
  const trueSpringData = colorSeasons.SPRING.subseasons.TRUE;

  // State for outfit data
  const [brightSpringOutfits, setBrightSpringOutfits] = useState<OutfitIdea[]>([]);
  const [trueSpringOutfits, setTrueSpringOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [brightSpringDoc, trueSpringDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "SPRING_BRIGHT")),
          getDoc(doc(db, "outfitIdeas", "SPRING_TRUE")),
        ]);

        setBrightSpringOutfits(brightSpringDoc.data()?.ideas || []);
        setTrueSpringOutfits(trueSpringDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const comparisonPoints = [
    {
      aspect: t("comparison.colorIntensity.label"),
      brightSpring: t("comparison.colorIntensity.brightSpring"),
      trueSpring: t("comparison.colorIntensity.trueSpring"),
      icon: IconPalette,
    },
    {
      aspect: t("comparison.undertone.label"),
      brightSpring: t("comparison.undertone.brightSpring"),
      trueSpring: t("comparison.undertone.trueSpring"),
      icon: IconEye,
    },
    {
      aspect: t("comparison.bestFor.label"),
      brightSpring: t("comparison.bestFor.brightSpring"),
      trueSpring: t("comparison.bestFor.trueSpring"),
      icon: IconSparkles,
    },
    {
      aspect: t("comparison.avoid.label"),
      brightSpring: t("comparison.avoid.brightSpring"),
      trueSpring: t("comparison.avoid.trueSpring"),
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            {t("hero.title")}
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            {t("hero.subtitle")}
          </Text>

          {/* Season Moodboard */}
          <Box mb="xl">
            <Box
              mih={{ base: "300px", md: "400px" }}
              style={{
                position: "relative",
                borderRadius: "1rem",
                overflow: "hidden",
              }}
            >
              <Image
                src="/SPRING.webp"
                alt="Spring season moodboard"
                width={800}
                height={400}
                style={{
                  borderRadius: "1rem",
                  objectFit: "cover",
                  objectPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            {t("quickComparison.title")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{brightSpringData.emoji}</Text>
                <Title order={3}>Bright Spring</Title>
                <Text size="sm" c="dimmed">
                  {brightSpringData.description.substring(0, 150)}...
                </Text>
                <Badge color="green" variant="light">
                  {t("quickComparison.brightSpring.badge")}
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{trueSpringData.emoji}</Text>
                <Title order={3}>True Spring</Title>
                <Text size="sm" c="dimmed">
                  {trueSpringData.description.substring(0, 150)}...
                </Text>
                <Badge color="green" variant="light">
                  {t("quickComparison.trueSpring.badge")}
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("keyDifferences.title")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="green" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="green" size="sm" mb="xs">
                      {t("labels.brightSpring")}:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.brightSpring}
                    </Text>
                    <Text fw={500} c="green" size="sm" mb="xs">
                      {t("labels.trueSpring")}:
                    </Text>
                    <Text size="sm">{point.trueSpring}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Detailed Color Palette Comparison */}
        <Tabs defaultValue="bright-spring" variant="outline">
          <Tabs.List justify="center" mb="xl">
            <Tabs.Tab value="bright-spring" leftSection={<IconDroplet size={16} />}>
              {t("palettes.brightSpring.tab")}
            </Tabs.Tab>
            <Tabs.Tab value="true-spring" leftSection={<IconDroplet size={16} />}>
              {t("palettes.trueSpring.tab")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="bright-spring">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                {t("palettes.brightSpring.title")}
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                {t("palettes.brightSpring.description")}
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.primaryColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {brightSpringData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.neutralColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {brightSpringData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.complementaryColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {brightSpringData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="true-spring">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                {t("palettes.trueSpring.title")}
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                {t("palettes.trueSpring.description")}
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.primaryColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {trueSpringData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.neutralColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {trueSpringData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    {t("palettes.sections.complementaryColors")}
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {trueSpringData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Characteristics Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("characteristics.title")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} mb="md" ta="center" c="green">
                Bright Spring
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    {t("characteristics.label")}:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {brightSpringData.characteristics.map((char, index) => (
                      <Badge key={index} color="green" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    {t("characteristics.sampleClothing")}:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {brightSpringData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="green">
                True Spring
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    {t("characteristics.label")}:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {trueSpringData.characteristics.map((char, index) => (
                      <Badge key={index} color="green" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    {t("characteristics.sampleClothing")}:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {trueSpringData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Outfit Samples */}
        <Divider my="xl" />
        <Box>
          <Title order={2} mb="lg" ta="center">
            {t("outfits.title")}
          </Title>
          <Text size="sm" c="dimmed" ta="center" mb="xl">
            {t("outfits.subtitle")}
          </Text>

          <Tabs defaultValue="bright-spring-outfits" variant="outline">
            <Tabs.List justify="center" mb="xl">
              <Tabs.Tab value="bright-spring-outfits" leftSection={<IconPhoto size={16} />}>
                {t("outfits.brightSpring")}
              </Tabs.Tab>
              <Tabs.Tab value="true-spring-outfits" leftSection={<IconPhoto size={16} />}>
                {t("outfits.trueSpring")}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="bright-spring-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>{t("outfits.loading")}</Text>
                </Box>
              ) : brightSpringOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={brightSpringOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">{t("outfits.noSamplesBright")}</Text>
                </Box>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="true-spring-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>{t("outfits.loading")}</Text>
                </Box>
              ) : trueSpringOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={trueSpringOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">{t("outfits.noSamplesTrue")}</Text>
                </Box>
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("homeTests.title")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">{t("homeTests.coralTest.title")}</Title>
              <Text size="sm" c="dimmed" mb="sm">
                {t("homeTests.coralTest.description")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>{t("labels.brightSpring")}:</strong> {t("homeTests.coralTest.brightSpring")}</List.Item>
                <List.Item><strong>{t("labels.trueSpring")}:</strong> {t("homeTests.coralTest.trueSpring")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">{t("homeTests.contrastTest.title")}</Title>
              <Text size="sm" c="dimmed" mb="sm">
                {t("homeTests.contrastTest.description")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>{t("labels.brightSpring")}:</strong> {t("homeTests.contrastTest.brightSpring")}</List.Item>
                <List.Item><strong>{t("labels.trueSpring")}:</strong> {t("homeTests.contrastTest.trueSpring")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              {t("homeTests.disclaimer")}
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="green.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              {t("cta.title")}
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              {t("cta.subtitle")}
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                {t("cta.primary")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                {t("cta.secondary")}
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/spring/bright"
                size="sm"
                color="gray"
              >
                {t("cta.viewBrightPalette")}
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/spring/true"
                size="sm"
                color="gray"
              >
                {t("cta.viewTruePalette")}
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
